import React, { useState, useEffect, useRef, useCallback } from 'react';
import { 
  Zap, 
  Eye, 
  BookOpen, 
  Clock, 
  Target, 
  Brain, 
  Settings, 
  Play, 
  Pause, 
  RotateCcw, 
  ChevronDown, 
  ChevronUp, 
  Plus, 
  Minus, 
  MoreVertical, 
  Edit3, 
  Save, 
  X, 
  CheckCircle, 
  AlertCircle, 
  Info, 
  Star, 
  Heart, 
  MessageSquare, 
  Share2, 
  Download, 
  Upload, 
  Filter, 
  Search, 
  SortAsc, 
  SortDesc, 
  Grid, 
  List, 
  Globe, 
  Lock, 
  Unlock, 
  Volume2, 
  VolumeX, 
  Headphones, 
  Mic, 
  Camera, 
  Video, 
  Music, 
  Image, 
  FileText, 
  ExternalLink, 
  Bookmark, 
  BookmarkCheck, 
  Archive, 
  Trash2, 
  Calendar, 
  Timer, 
  Hourglass, 
  Activity, 
  BarChart3, 
  PieChart, 
  LineChart, 
  TrendingUp, 
  TrendingDown, 
  ArrowUp, 
  ArrowDown, 
  ArrowLeft, 
  ArrowRight, 
  ArrowUpRight, 
  ArrowDownRight, 
  ArrowUpLeft, 
  ArrowDownLeft, 
  Maximize, 
  Minimize, 
  Maximize2, 
  Minimize2, 
  RotateCw, 
  RefreshCw, 
  RefreshCcw, 
  Undo, 
  Redo, 
  Copy, 
  Scissors, 
  Clipboard, 
  ClipboardCheck, 
  ClipboardCopy, 
  ClipboardList, 
  ClipboardPaste, 
  ClipboardX, 
  File, 
  FileCheck, 
  FileEdit, 
  FileMinus, 
  FilePlus, 
  FileSearch, 
  FileText as FileTextIcon, 
  FileX, 
  Folder, 
  FolderCheck, 
  FolderEdit, 
  FolderMinus, 
  FolderOpen, 
  FolderPlus, 
  FolderSearch, 
  FolderX, 
  Sun, 
  Moon, 
  Cloud, 
  CloudRain, 
  CloudSnow, 
  CloudLightning, 
  CloudDrizzle, 
  CloudHail, 
  CloudFog, 
  CloudSun, 
  CloudMoon, 
  Sunrise, 
  Sunset, 
  StarHalf, 
  StarOff, 
  Sparkle, 
  Sparkles, 
  Diamond, 
  Gem, 
  Coins, 
  Banknote, 
  CreditCard, 
  Wallet, 
  PiggyBank, 
  ShoppingCart, 
  ShoppingBag, 
  Package, 
  Truck, 
  Home, 
  Building, 
  Square, 
  Circle, 
  Triangle, 
  Hexagon, 
  Octagon, 
  Pentagon, 
  CheckCircle as CheckCircleIcon
} from 'lucide-react';

interface ReadingMode {
  id: string;
  name: string;
  description: string;
  icon: string;
  speed: number; // words per minute
  focus: 'comprehension' | 'speed' | 'balance';
  features: string[];
  color: string;
  bgColor: string;
}

interface ReadingModesProps {
  content: string;
  onModeChange?: (mode: ReadingMode) => void;
  onSpeedChange?: (speed: number) => void;
  onProgressUpdate?: (progress: { wordsRead: number; timeSpent: number; wpm: number }) => void;
}

const ReadingModes: React.FC<ReadingModesProps> = ({
  content,
  onModeChange,
  onSpeedChange,
  onProgressUpdate
}) => {
  const [currentMode, setCurrentMode] = useState<ReadingMode | null>(null);
  const [isActive, setIsActive] = useState(false);
  const [customSpeed, setCustomSpeed] = useState(250);
  const [wordsRead, setWordsRead] = useState(0);
  const [timeSpent, setTimeSpent] = useState(0);
  const [currentWordIndex, setCurrentWordIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [highlightedWords, setHighlightedWords] = useState<Set<number>>(new Set());
  const [readingProgress, setReadingProgress] = useState(0);
  const [comprehensionScore, setComprehensionScore] = useState(0);
  const [focusLevel, setFocusLevel] = useState(100);
  const [distractionLevel, setDistractionLevel] = useState(0);

  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const startTimeRef = useRef<number>(0);
  const wordsRef = useRef<string[]>([]);
  const contentRef = useRef<HTMLDivElement>(null);

  const readingModes: ReadingMode[] = [
    {
      id: 'skim',
      name: 'Skim Mode',
      description: 'Quick overview reading for main ideas and key points',
      icon: 'Zap',
      speed: 500,
      focus: 'speed',
      features: ['Keyword highlighting', 'Key sentence extraction', 'Rapid scanning'],
      color: 'text-blue-600 dark:text-blue-400',
      bgColor: 'bg-blue-100 dark:bg-blue-900'
    },
    {
      id: 'deep',
      name: 'Deep Read',
      description: 'Comprehensive reading with full comprehension focus',
      icon: 'Brain',
      speed: 150,
      focus: 'comprehension',
      features: ['Full text analysis', 'Comprehension checks', 'Note-taking'],
      color: 'text-purple-600 dark:text-purple-400',
      bgColor: 'bg-purple-100 dark:bg-purple-900'
    },
    {
      id: 'speed',
      name: 'Speed Read',
      description: 'Fast reading with maintained comprehension',
      icon: 'Eye',
      speed: 400,
      focus: 'balance',
      features: ['Rapid word processing', 'Focus training', 'Speed optimization'],
      color: 'text-green-600 dark:text-green-400',
      bgColor: 'bg-green-100 dark:bg-green-900'
    },
    {
      id: 'custom',
      name: 'Custom Mode',
      description: 'Personalized reading experience',
      icon: 'Settings',
      speed: customSpeed,
      focus: 'balance',
      features: ['Adjustable speed', 'Custom settings', 'Personal preferences'],
      color: 'text-orange-600 dark:text-orange-400',
      bgColor: 'bg-orange-100 dark:bg-orange-900'
    }
  ];

  useEffect(() => {
    // Split content into words for processing
    wordsRef.current = content.split(/\s+/).filter(word => word.length > 0);
  }, [content]);

  useEffect(() => {
    if (isActive && currentMode && !isPaused) {
      startTimeRef.current = Date.now();
      timerRef.current = setInterval(() => {
        const elapsed = Date.now() - startTimeRef.current;
        setTimeSpent(elapsed);
        
        // Calculate words read based on time and speed
        const expectedWords = Math.floor((elapsed / 60000) * currentMode.speed);
        setWordsRead(Math.min(expectedWords, wordsRef.current.length));
        setCurrentWordIndex(Math.min(expectedWords, wordsRef.current.length - 1));
        
        // Update progress
        const progress = (wordsRead / wordsRef.current.length) * 100;
        setReadingProgress(progress);
        
        // Calculate WPM
        const wpm = wordsRead > 0 ? Math.floor((wordsRead / (elapsed / 60000))) : 0;
        
        onProgressUpdate?.({
          wordsRead,
          timeSpent: elapsed,
          wpm
        });
        
        // Update comprehension score based on focus and reading mode
        updateComprehensionScore();
        
      }, 100);
    } else {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [isActive, currentMode, isPaused, wordsRead, onProgressUpdate]);

  const updateComprehensionScore = useCallback(() => {
    let baseScore = 100;
    
    // Adjust based on reading mode
    if (currentMode?.focus === 'speed') {
      baseScore -= 20; // Speed reading reduces comprehension
    } else if (currentMode?.focus === 'comprehension') {
      baseScore += 10; // Deep reading improves comprehension
    }
    
    // Adjust based on focus level
    baseScore = baseScore * (focusLevel / 100);
    
    // Adjust based on distraction level
    baseScore = baseScore * (1 - distractionLevel / 100);
    
    setComprehensionScore(Math.max(0, Math.min(100, baseScore)));
  }, [currentMode, focusLevel, distractionLevel]);

  const handleModeSelect = (mode: ReadingMode) => {
    setCurrentMode(mode);
    onModeChange?.(mode);
    onSpeedChange?.(mode.speed);
  };

  const handleStartReading = () => {
    if (!currentMode) return;
    
    setIsActive(true);
    setIsPaused(false);
    setWordsRead(0);
    setTimeSpent(0);
    setCurrentWordIndex(0);
    setReadingProgress(0);
    setHighlightedWords(new Set());
  };

  const handlePauseReading = () => {
    setIsPaused(!isPaused);
  };

  const handleStopReading = () => {
    setIsActive(false);
    setIsPaused(false);
    setWordsRead(0);
    setTimeSpent(0);
    setCurrentWordIndex(0);
    setReadingProgress(0);
    setHighlightedWords(new Set());
  };

  const handleResetReading = () => {
    handleStopReading();
    setCurrentMode(null);
  };

  const handleSpeedChange = (speed: number) => {
    setCustomSpeed(speed);
    if (currentMode?.id === 'custom') {
      const updatedMode = { ...currentMode, speed };
      setCurrentMode(updatedMode);
      onModeChange?.(updatedMode);
      onSpeedChange?.(speed);
    }
  };

  const handleWordClick = (wordIndex: number) => {
    if (currentMode?.focus === 'comprehension') {
      setHighlightedWords(prev => {
        const newSet = new Set(prev);
        if (newSet.has(wordIndex)) {
          newSet.delete(wordIndex);
        } else {
          newSet.add(wordIndex);
        }
        return newSet;
      });
    }
  };

  const getIconComponent = (iconName: string) => {
    const iconMap: { [key: string]: React.ComponentType<any> } = {
      Zap,
      Brain,
      Eye,
      Settings,
      BookOpen,
      Clock,
      Target,
      Play,
      Pause,
      RotateCcw,
      ChevronDown,
      ChevronUp,
      Plus,
      Minus,
      MoreVertical,
      Edit3,
      Save,
      X,
      CheckCircle,
      AlertCircle,
      Info,
      Star,
      Heart,
      MessageSquare,
      Share2,
      Download,
      Upload,
      Filter,
      Search,
      SortAsc,
      SortDesc,
      Grid,
      List,
      Globe,
      Lock,
      Unlock,
      Volume2,
      VolumeX,
      Headphones,
      Mic,
      Camera,
      Video,
      Music,
      Image,
      FileText,
      ExternalLink,
      Bookmark,
      BookmarkCheck,
      Archive,
      Trash2,
      Calendar,
      Timer,
      Hourglass,
      Activity,
      BarChart3,
      PieChart,
      LineChart,
      TrendingUp,
      TrendingDown,
      ArrowUp,
      ArrowDown,
      ArrowLeft,
      ArrowRight,
      ArrowUpRight,
      ArrowDownRight,
      ArrowUpLeft,
      ArrowDownLeft,
      Maximize,
      Minimize,
      Maximize2,
      Minimize2,
      RotateCw,
      RefreshCw,
      RefreshCcw,
      Undo,
      Redo,
      Copy,
      Scissors,
      Clipboard,
      ClipboardCheck,
      ClipboardCopy,
      ClipboardList,
      ClipboardPaste,
      ClipboardX,
      File,
      FileCheck,
      FileEdit,
      FileMinus,
      FilePlus,
      FileSearch,
      FileTextIcon,
      FileX,
      Folder,
      FolderCheck,
      FolderEdit,
      FolderMinus,
      FolderOpen,
      FolderPlus,
      FolderSearch,
      FolderX,
      Sun,
      Moon,
      Cloud,
      CloudRain,
      CloudSnow,
      CloudLightning,
      CloudDrizzle,
      CloudHail,
      CloudFog,
      CloudSun,
      CloudMoon,
      Sunrise,
      Sunset,
      StarHalf,
      StarOff,
      Sparkle,
      Sparkles,
      Diamond,
      Gem,
      Coins,
      Banknote,
      CreditCard,
      Wallet,
      PiggyBank,
      ShoppingCart,
      ShoppingBag,
      Package,
      Truck,
      Home,
      Building,
      Square,
      Circle,
      Triangle,
      Hexagon,
      Octagon,
      Pentagon,
      CheckCircleIcon
    };
    
    return iconMap[iconName] || BookOpen;
  };

  const formatTime = (milliseconds: number): string => {
    const seconds = Math.floor(milliseconds / 1000);
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const getCurrentWords = () => {
    if (currentMode?.id === 'skim') {
      // For skim mode, show only key sentences
      return wordsRef.current.filter((word, index) => {
        // Simple keyword detection for demo
        return word.length > 6 || word.match(/[A-Z]/) || index % 10 === 0;
      });
    }
    return wordsRef.current;
  };

  return (
    <div className="max-w-6xl mx-auto p-6">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
          Reading Modes
        </h1>
        <p className="text-gray-600 dark:text-gray-400">
          Choose your optimal reading experience based on your goals and content type
        </p>
      </div>

      {/* Mode Selection */}
      {!isActive && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {readingModes.map((mode) => {
            const IconComponent = getIconComponent(mode.icon);
            return (
              <div
                key={mode.id}
                className={`p-6 rounded-lg border-2 cursor-pointer transition-all duration-200 hover:shadow-lg ${
                  currentMode?.id === mode.id
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900'
                    : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                }`}
                onClick={() => handleModeSelect(mode)}
              >
                <div className="flex items-center mb-4">
                  <div className={`p-3 rounded-lg ${mode.bgColor}`}>
                    <IconComponent className={`h-8 w-8 ${mode.color}`} />
                  </div>
                  <div className="ml-4">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      {mode.name}
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {mode.speed} WPM
                    </p>
                  </div>
                </div>
                
                <p className="text-gray-600 dark:text-gray-400 mb-4">
                  {mode.description}
                </p>
                
                <div className="space-y-2">
                  <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Features:
                  </p>
                  <ul className="text-sm text-gray-600 dark:text-gray-400 space-y-1">
                    {mode.features.map((feature, index) => (
                      <li key={index} className="flex items-center">
                        <CheckCircle className="h-3 w-3 mr-2 text-green-500" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Reading Controls */}
      {currentMode && (
        <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm border border-gray-200 dark:border-gray-700 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <div className={`p-2 rounded-lg ${currentMode.bgColor} mr-3`}>
                {React.createElement(getIconComponent(currentMode.icon), {
                  className: `h-6 w-6 ${currentMode.color}`
                })}
              </div>
              <div>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                  {currentMode.name}
                </h2>
                <p className="text-gray-600 dark:text-gray-400">
                  {currentMode.speed} WPM • {currentMode.focus} focus
                </p>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              {!isActive ? (
                <button
                  onClick={handleStartReading}
                  className="px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 flex items-center"
                >
                  <Play className="h-4 w-4 mr-2" />
                  Start Reading
                </button>
              ) : (
                <>
                  <button
                    onClick={handlePauseReading}
                    className="px-4 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 flex items-center"
                  >
                    {isPaused ? (
                      <>
                        <Play className="h-4 w-4 mr-2" />
                        Resume
                      </>
                    ) : (
                      <>
                        <Pause className="h-4 w-4 mr-2" />
                        Pause
                      </>
                    )}
                  </button>
                  <button
                    onClick={handleStopReading}
                    className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 flex items-center"
                  >
                    <X className="h-4 w-4 mr-2" />
                    Stop
                  </button>
                </>
              )}
              <button
                onClick={handleResetReading}
                className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 flex items-center"
              >
                <RotateCcw className="h-4 w-4 mr-2" />
                Reset
              </button>
            </div>
          </div>

          {/* Progress Bar */}
          <div className="mb-4">
            <div className="flex justify-between text-sm text-gray-600 dark:text-gray-400 mb-2">
              <span>Progress: {Math.round(readingProgress)}%</span>
              <span>{wordsRead} / {wordsRef.current.length} words</span>
            </div>
            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
              <div
                className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                style={{ width: `${readingProgress}%` }}
              ></div>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-gray-900 dark:text-white">
                {formatTime(timeSpent)}
              </div>
              <div className="text-xs text-gray-600 dark:text-gray-400">Time</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-gray-900 dark:text-white">
                {Math.floor((wordsRead / (timeSpent / 60000)) || 0)}
              </div>
              <div className="text-xs text-gray-600 dark:text-gray-400">WPM</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-gray-900 dark:text-white">
                {Math.round(comprehensionScore)}%
              </div>
              <div className="text-xs text-gray-600 dark:text-gray-400">Comprehension</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-gray-900 dark:text-white">
                {focusLevel}%
              </div>
              <div className="text-xs text-gray-600 dark:text-gray-400">Focus</div>
            </div>
          </div>
        </div>
      )}

      {/* Reading Content */}
      {isActive && currentMode && (
        <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm border border-gray-200 dark:border-gray-700 mb-6">
          <div className="mb-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              Reading Content
            </h3>
            <div className="flex items-center gap-4 text-sm text-gray-600 dark:text-gray-400">
              <span>Mode: {currentMode.name}</span>
              <span>Speed: {currentMode.speed} WPM</span>
              <span>Focus: {currentMode.focus}</span>
            </div>
          </div>
          
          <div 
            ref={contentRef}
            className="prose prose-lg max-w-none dark:prose-invert"
            style={{ 
              fontSize: currentMode.id === 'skim' ? '1.2rem' : '1rem',
              lineHeight: currentMode.focus === 'comprehension' ? '1.8' : '1.5'
            }}
          >
            {getCurrentWords().map((word, index) => (
              <span
                key={index}
                className={`cursor-pointer transition-colors duration-200 ${
                  index <= currentWordIndex 
                    ? 'text-gray-900 dark:text-white' 
                    : 'text-gray-400 dark:text-gray-600'
                } ${
                  highlightedWords.has(index) 
                    ? 'bg-yellow-200 dark:bg-yellow-800 px-1 rounded' 
                    : ''
                } ${
                  index === currentWordIndex 
                    ? 'bg-blue-200 dark:bg-blue-800 px-1 rounded font-semibold' 
                    : ''
                }`}
                onClick={() => handleWordClick(index)}
              >
                {word}{' '}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Settings Panel */}
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm border border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            Reading Settings
          </h3>
          <button
            onClick={() => setShowSettings(!showSettings)}
            className="p-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
          >
            <Settings className="h-5 w-5" />
          </button>
        </div>

        {showSettings && (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Custom Reading Speed (WPM)
              </label>
              <div className="flex items-center gap-4">
                <input
                  type="range"
                  min="100"
                  max="800"
                  value={customSpeed}
                  onChange={(e) => handleSpeedChange(parseInt(e.target.value))}
                  className="flex-1"
                />
                <span className="text-sm text-gray-600 dark:text-gray-400 min-w-[60px]">
                  {customSpeed} WPM
                </span>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Focus Level: {focusLevel}%
              </label>
              <input
                type="range"
                min="0"
                max="100"
                value={focusLevel}
                onChange={(e) => setFocusLevel(parseInt(e.target.value))}
                className="w-full"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Distraction Level: {distractionLevel}%
              </label>
              <input
                type="range"
                min="0"
                max="100"
                value={distractionLevel}
                onChange={(e) => setDistractionLevel(parseInt(e.target.value))}
                className="w-full"
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReadingModes;
